/*
 * User navigation history stored here.
 *
 * Cookie and tenancy settings are also stored here.
 */

const getDefaultState = () => {
  return {
    current_page: '1-0',
    next_page: null,
    current_module: null,

    page_completed: false,

    progress: 0,
    std_progress: 0,

    navigation_history: {
      '1-0': null,
    },

    optional_history: {},
    optional_completed: [],
    optional_available: [],
    optional_mode: false,

    /*
     * Snapshot of the real core-course position while the user
     * temporarily works through a bonus module.
     *
     * Optional navigation must never replace the user's core
     * current page/module.
     */
    core_page_before_optional: null,
    core_module_before_optional: null,
    core_progress_before_optional: null,

    hydrating: false,
    review_mode: false,

    notifier_info: null,
    display_notification: false,

    going_back: false,

    last_viewed_page: null,
    next_viewed_page: null,

    menu_items: [],

    module_color: null,

    pendingRequests: 0,
  }
}

// Initial state
export const state = getDefaultState()

const OPTIONAL_MAX_INDEX = {
  '3': 8,
  '4': 9,
  '4A': 7,
  '5A': 4,
}

// Check whether an object has a key
const containsKey = function (obj, key) {
  try {
    if (Object.keys(obj).includes(key)) {
      return true
    }

    return false
  } catch (e) {
    return false
  }
}

export const mutations = {
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },

  INCREMENT_PENDING(state) {
    state.pendingRequests++
  },

  DECREMENT_PENDING(state) {
    if (state.pendingRequests > 0) {
      state.pendingRequests--
    }
  },

  setReviewModeStatus(state, status) {
    state.review_mode = !!status
  },

  setHydratingStatus(state, val) {
    state.hydrating = !!val
  },

  // Set the current page
  setCurrentPage(state, current_page) {
    state.current_page = current_page
  },

  // Set the current module if it exists
  setCurrentModule(state, current_module) {
    state.current_module = current_module
  },

  // Set the module colour if it exists
  setCurrentModuleColor(state, module_color) {
    state.module_color = module_color
  },

  // Set the next page to move to on clicking next
  setNextPage(state, next_page) {
    state.next_page = next_page
  },

  // Set the next page used in carousels
  setNextViewedPage(state, next_viewed_page) {
    state.next_viewed_page = next_viewed_page
  },

  // Set the last seen page
  setLastViewedPage(state, last_viewed_page) {
    state.last_viewed_page = last_viewed_page
  },

  // Set the current page as completed
  setPageCompleted(state, page_completed) {
    state.page_completed = page_completed
  },

  // Set the progress bar value for this page
  setPageProgress(state, progress) {
    state.progress = progress || 0

    // Store the user's progress in standard mode
    if (state.optional_mode === false) {
      state.std_progress = progress
    }
  },

  // Store answers for the current page
  setNavigationHistory(state, item) {
    if (state.optional_mode) {
      state.optional_history[state.current_page] = item
    } else {
      state.navigation_history[state.current_page] = item
    }
  },

  setCurrentModuleName(state, module_name) {
    state.module_name = module_name
  },

  // Set notification information for the page
  setNotifierInfo(state, notifier_info) {
    state.notifier_info = notifier_info
  },

  // Determine whether the user is navigating backwards
  setGoingBack(state, going_back) {
    state.going_back = going_back
  },

  // Add a page to the user's navigation history
  setPageHistoryViewed(state, page) {
    const history = state.optional_mode
      ? state.optional_history
      : state.navigation_history

    if (!containsKey(history, page)) {
      history[page] = ''
    }
  },

  // Mark optional mode status
  setOptionalModeStatus(state, status) {
    state.optional_mode = status
  },
    /**
   * Remember the exact core-course position before entering
   * an optional module.
   *
   * This snapshot is local navigation state only. It is not part
   * of optional_history and does not affect the backend course path.
   */
    setCorePositionBeforeOptional(
      state,
      position
    ) {
      const value =
        position &&
        typeof position === 'object'
          ? position
          : {}
  
      state.core_page_before_optional =
        value.page != null
          ? String(value.page)
          : null
  
      state.core_module_before_optional =
        value.module != null
          ? String(value.module)
          : null
  
      const progress =
        Number(value.progress)
  
      state.core_progress_before_optional =
        Number.isFinite(progress)
          ? progress
          : null
    },
  
    /**
     * Clear the temporary core-position snapshot after leaving
     * optional mode.
     */
    clearCorePositionBeforeOptional(
      state
    ) {
      state.core_page_before_optional =
        null
  
      state.core_module_before_optional =
        null
  
      state.core_progress_before_optional =
        null
    },

  // Mark optional modules as completed
  setCompletedOptionalModules(state, optional_completed) {
    state.optional_completed = optional_completed
  },

  // Mark optional modules as available
  setAvailableOptionalModules(state, optional_available) {
    state.optional_available = optional_available
  },

  resetNavigationHistory(state, navigation_history) {
    /*
     * This mutation must guarantee a usable navigation map.
     *
     * When the user has made zero progress, navigation history may
     * return from the backend as null, an empty string or an empty
     * object. Treat those values as an untouched profile.
     */

    let parsed = helpers.parseIfString(navigation_history)

    // Accept { main: '...json...' }, { main: {...} }, or a plain map
    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.main != null
    ) {
      parsed = helpers.parseIfString(parsed.main)
    }

    if (
      !parsed ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed)
    ) {
      parsed = {
        '1-0': null,
      }
    }

    if (Object.keys(parsed).length === 0) {
      parsed = {
        '1-0': null,
      }
    }

    if (
      !Object.prototype.hasOwnProperty.call(
        parsed,
        '1-0'
      )
    ) {
      parsed['1-0'] = null
    }

    state.navigation_history = parsed
  },

  // Set whether the page notification should be open
  setDisplayNotification(state, display_notification) {
    state.display_notification = display_notification
  },

  // Restore optional answers, completion and availability
  resetOptionalHistory(state, history) {
    const parsedHistory =
      helpers.parseIfString(history) || {}

    const parsedOptionalHistory =
      helpers.parseIfString(
        parsedHistory.optional_history
      )

    if (
      parsedOptionalHistory &&
      typeof parsedOptionalHistory === 'object'
    ) {
      state.optional_history = parsedOptionalHistory
    } else {
      state.optional_history =
        state.optional_history || {}
    }

    const parsedCompleted =
      helpers.parseIfString(
        parsedHistory.optional_completed
      )

    if (Array.isArray(parsedCompleted)) {
      state.optional_completed =
        parsedCompleted.map(String)
    } else {
      state.optional_completed =
        state.optional_completed || []
    }

    const parsedAvailable =
      helpers.parseIfString(
        parsedHistory.optional_available
      )

    if (Array.isArray(parsedAvailable)) {
      state.optional_available =
        parsedAvailable.map(String)
    } else {
      state.optional_available =
        state.optional_available || []
    }
  },

  // Set dashboard menu items
  setMenuItems(state, menu) {
    state.menu_items = menu
  },

  // Set optional modules as available
  setOptionalAvailable(state, optional) {
    state.optional_available = optional
  },

  removeOptionalModule(state, module) {
    const moduleId = String(module)

    state.optional_available = (
      state.optional_available || []
    )
      .map(String)
      .filter(
        (item) =>
          item !== moduleId
      )

    state.optional_completed = (
      state.optional_completed || []
    )
      .map(String)
      .filter(
        (item) =>
          item !== moduleId
      )

    /*
     * Remove only this module's optional-page history.
     * Do not touch another optional module.
     */
    const nextOptionalHistory = {}

    Object.keys(
      state.optional_history || {}
    ).forEach((page) => {
      const pageModule =
        String(page).split('-')[0]

      if (pageModule !== moduleId) {
        nextOptionalHistory[page] =
          state.optional_history[page]
      }
    })

    state.optional_history =
      nextOptionalHistory
  },
}

export const actions = {
  // Reset state when returning to the main page
  resetState({ commit }) {
    commit('RESET_STATE')
  },

  async replaceNavigationHistory(
    { dispatch, commit },
    history
  ) {
    commit('setHydratingStatus', true)

    try {
      /*
       * Always hydrate with a usable main navigation map.
       *
       * Some profiles return:
       * - navigation_history = null
       * - navigation_history = "{}"
       * - navigation_history = { main: {} }
       */

      const rawMain =
        history && history.main != null
          ? history.main
          : null

      let mainMap =
        helpers.parseIfString(rawMain)

      const isObject =
        mainMap &&
        typeof mainMap === 'object' &&
        !Array.isArray(mainMap)

      const isEmptyObject =
        isObject &&
        Object.keys(mainMap).length === 0

      if (!isObject || isEmptyObject) {
        mainMap = {
          '1-0': null,
        }
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          mainMap,
          '1-0'
        )
      ) {
        mainMap['1-0'] = null
      }

      commit(
        'resetNavigationHistory',
        mainMap
      )

      commit(
        'resetOptionalHistory',
        history || {}
      )

      const last =
        (history && history.last_page) ||
        '1-0'

      commit(
        'setCurrentPage',
        last
      )

      commit(
        'setCurrentModule',
        last === 'Review'
          ? 'Review'
          : String(last).split('-')[0]
      )

      await dispatch(
        'hydrateProgressFromSlug',
        last
      )

      await dispatch(
        'setNavigationArrowPages'
      )

      await dispatch(
        'processMenuItems'
      )

      await dispatch(
        'returnToLastPage'
      )
    } finally {
      commit(
        'setHydratingStatus',
        false
      )
    }
  },

  setReviewMode({ commit }, status) {
    commit(
      'setReviewModeStatus',
      !!status
    )
  },

  // Add an existing answer to the history
  addToHistory({ commit }, payload) {
    commit(
      'setPageHistoryViewed',
      payload
    )
  },

  async hydrateProgressFromSlug(
    { commit },
    slug
  ) {
    if (!slug) {
      return
    }

    try {
      const doc =
        await this.$content(slug).fetch()

      const progress =
        Number(
          doc && doc.progress
        ) || 0

      commit(
        'setPageProgress',
        progress
      )
    } catch (e) {
      // Leave progress unchanged if content cannot be loaded
    }
  },

    /**
   * Set the currently visible page.
   *
   * Optional pages may use current_page/current_module while they
   * are open, but they must never persist themselves as the user's
   * main-course last_page.
   */
    async setCurrentPage(
      {
        commit,
        state,
        rootGetters,
        dispatch,
      },
      page
    ) {
      /*
       * A calculated destination belongs to the previously visible
       * page and must not leak into the newly loaded page.
       */
      commit(
        'setNextPage',
        null
      )
  
      /*
       * The new page or its child component establishes completion.
       */
      commit(
        'setPageCompleted',
        false
      )
  
      /*
       * Arrow destinations are recalculated for the new page.
       */
      commit(
        'setLastViewedPage',
        null
      )
  
      commit(
        'setNextViewedPage',
        null
      )
  
      commit(
        'setCurrentPage',
        page
      )
  
      try {
        const moduleNo =
          String(page).split('-')[0]
  
        if (
          state.current_module !==
          moduleNo
        ) {
          commit(
            'setCurrentModule',
            moduleNo
          )
  
          /*
           * While an optional module is visible, rebuilding the menu
           * is harmless, but its module number must never subsequently
           * be persisted as the main-course position.
           */
          await dispatch(
            'processMenuItems'
          )
  
          const labels =
            rootGetters[
              'content/getLabels'
            ] || {}
  
          const moduleLabels =
            labels.modules || {}
  
          const moduleColors =
            labels.colors || {}
  
          commit(
            'setCurrentModuleName',
            moduleLabels[moduleNo] ||
              moduleNo
          )
  
          /*
           * CRITICAL:
           *
           * Never save main navigation while an optional module is
           * active. Otherwise /page/3-x?optional=1 can overwrite
           * main last_page with 3-x.
           */
          if (
            !state.hydrating &&
            state.optional_mode !== true
          ) {
            await dispatch(
              'storeNavigationHistory'
            )
          }
  
          commit(
            'setCurrentModuleColor',
            moduleColors[moduleNo] ||
              null
          )
        }
      } catch (error) {
        console.log(
          'page does not exist, skipping',
          error
        )
      }
    },

  // Save the user's optional navigation history
  async storeOptionalHistory({ state }) {
    await this.$axios
      .$post(
        this.$config.BASE_API +
          '/activity/commit_optional_history',
        {
          history:
            state.optional_history,
          completed:
            state.optional_completed,
          available:
            state.optional_available,
        }
      )
      .catch(() => {})
  },

    /**
   * Save the user's MAIN navigation history.
   *
   * Optional modules have their own persistence endpoint and must
   * never update main navigation_history or main last_page.
   */
    async storeNavigationHistory({
      state,
    }) {
      /*
       * Never persist transient hydration state.
       */
      if (state.hydrating) {
        return
      }
  
      /*
       * CRITICAL OPTIONAL-MODULE ISOLATION:
       *
       * current_page/current_module temporarily describe the bonus
       * page while optional mode is active. Saving here would write
       * an optional slug such as 3-8 as the main-course last_page.
       */
      if (state.optional_mode) {
        return
      }
  
      const navigationHistory =
        state.navigation_history || {}
  
      const keys =
        Object.keys(
          navigationHistory
        )
  
      const looksPristine =
        keys.length === 1 &&
        keys[0] === '1-0' &&
        (
          state.current_page ===
            '1-0' ||
          !state.current_page
        )
  
      if (looksPristine) {
        return
      }
  
      try {
        await this.$axios.$post(
          this.$config.BASE_API +
            '/activity/commit_navigation_history',
          {
            navigation_history:
              JSON.stringify(
                state.navigation_history
              ),
  
            last_page:
              state.current_page,
          }
        )
      } catch (_) {
        /*
         * Navigation persistence must not prevent the user
         * from continuing through MyUSE.
         */
      }
    },

  // Mark an optional module as completed
  async setOptionalModuleAsCompleted({
    dispatch,
    state,
    commit,
  }) {
    const completed = [
      ...new Set(
        state.optional_completed || []
      ),
    ]

    const module =
      state.current_module

    if (
      module &&
      !completed.includes(module)
    ) {
      completed.push(module)
    }

    let available = [
      ...new Set(
        state.optional_available || []
      ),
    ]

    available =
      available.filter(
        (item) =>
          item !== module
      )

    commit(
      'setCompletedOptionalModules',
      completed
    )

    commit(
      'setAvailableOptionalModules',
      available
    )

    await dispatch(
      'storeOptionalHistory'
    )

    await dispatch(
      'processMenuItems'
    )
  },

  // Set the next page and mark the current page as complete
  setNextPage({ commit }, page) {
    commit(
      'setNextPage',
      page || null
    )

    commit(
      'setPageCompleted',
      Boolean(page)
    )
  },

     /**
   * Return to the dashboard.
   *
   * There are two distinct flows:
   *
   * 1. OPTIONAL MODE
   *    Restore the exact core-course position that existed before
   *    the bonus module was opened. Never persist the optional page
   *    as the main last_page.
   *
   * 2. STANDARD MODE
   *    Preserve the special terminal-page handling for 2-9.
   */
  async goToMenu({
    commit,
    state,
    dispatch,
  }) {
    /*
     * ----------------------------------------------------------
     * OPTIONAL MODULE EXIT
     * ----------------------------------------------------------
     */
    if (state.optional_mode === true) {
      /*
       * Save bonus answers/completion/availability first.
       */
      await dispatch(
        'storeOptionalHistory'
      )

      const restorePage =
        state.core_page_before_optional ||
        null

      const restoreModule =
        state.core_module_before_optional ||
        null

      const restoreProgress =
        state.core_progress_before_optional

      /*
       * Exit optional context BEFORE restoring normal course state.
       */
      commit(
        'setOptionalModeStatus',
        false
      )

      commit(
        'setReviewModeStatus',
        false
      )

      commit(
        'setGoingBack',
        false
      )

      commit(
        'setNextPage',
        null
      )

      commit(
        'setPageCompleted',
        false
      )

      /*
       * Restore the exact core page/module from which the user chose
       * to open the bonus module.
       */
      if (restorePage) {
        commit(
          'setCurrentPage',
          restorePage
        )
      }

      if (restoreModule) {
        commit(
          'setCurrentModule',
          restoreModule
        )
      }

      if (
        restoreProgress !== null &&
        restoreProgress !== undefined
      ) {
        commit(
          'setPageProgress',
          restoreProgress
        )
      } else if (restorePage) {
        await dispatch(
          'hydrateProgressFromSlug',
          restorePage
        )
      }

      /*
       * Optional history must never change main navigation history.
       * We are simply rebuilding the dashboard using the restored
       * core position.
       */
      await dispatch(
        'setNavigationArrowPages'
      )

      await dispatch(
        'processMenuItems'
      )

      commit(
        'clearCorePositionBeforeOptional'
      )

      if (
        !this.$router ||
        (
          this.$router.currentRoute &&
          this.$router.currentRoute.path ===
            '/dashboard'
        )
      ) {
        return
      }

      try {
        await this.$router.push({
          path: '/dashboard',
        })
      } catch (error) {
        const duplicateNavigation =
          error &&
          (
            error.name ===
              'NavigationDuplicated' ||
            String(
              error.message || ''
            ).includes(
              'Avoided redundant navigation'
            )
          )

        if (!duplicateNavigation) {
          throw error
        }
      }

      return
    }

    /*
     * ----------------------------------------------------------
     * STANDARD COURSE EXIT
     * ----------------------------------------------------------
     */

    const isTerminalDecisionPage =
      String(state.current_page) ===
      '2-9'

    const isStandardJourney =
      state.review_mode !== true

    const hasCalculatedDestination =
      state.page_completed === true &&
      Boolean(state.next_page)

    /*
     * Page 2-9 has genuinely reached the end of module 2.
     *
     * If the user chooses Menu instead of Continue, expose the
     * already-calculated next core destination.
     */
    if (
      isTerminalDecisionPage &&
      isStandardJourney &&
      hasCalculatedDestination
    ) {
      commit(
        'setPageProgress',
        100
      )

      commit(
        'setPageHistoryViewed',
        String(state.next_page)
      )
    }

    await dispatch(
      'storeNavigationHistory'
    )

    await dispatch(
      'storeOptionalHistory'
    )

    await dispatch(
      'processMenuItems'
    )

    commit(
      'setReviewModeStatus',
      false
    )

    commit(
      'setOptionalModeStatus',
      false
    )

    commit(
      'setGoingBack',
      false
    )

    commit(
      'setNextPage',
      null
    )

    commit(
      'setPageCompleted',
      false
    )

    if (
      !this.$router ||
      (
        this.$router.currentRoute &&
        this.$router.currentRoute.path ===
          '/dashboard'
      )
    ) {
      return
    }

    try {
      await this.$router.push({
        path: '/dashboard',
      })
    } catch (error) {
      const duplicateNavigation =
        error &&
        (
          error.name ===
            'NavigationDuplicated' ||
          String(
            error.message || ''
          ).includes(
            'Avoided redundant navigation'
          )
        )

      if (!duplicateNavigation) {
        throw error
      }
    }
  },

  
  /**
   * Enable or disable optional-module mode.
   *
   * When entering optional mode, preserve the exact core-course
   * position so finishing or leaving the bonus module can return
   * the dashboard to the same core journey.
   */
  async setOptionalModStatus(
    {
      commit,
      state,
    },
    optionalMode
  ) {
    const enabled =
      optionalMode === true ||
      optionalMode === 1 ||
      optionalMode === '1' ||
      optionalMode === 'true'

    const enteringOptionalMode =
      enabled &&
      state.optional_mode !== true

    /*
     * Snapshot the real core position BEFORE optional_mode becomes
     * true and before an optional page changes current_page/module.
     */
    if (enteringOptionalMode) {
      commit(
        'setCorePositionBeforeOptional',
        {
          page:
            state.current_page,

          module:
            state.current_module,

          progress:
            state.std_progress,
        }
      )
    }

    commit(
      'setOptionalModeStatus',
      enabled
    )

    if (
      enteringOptionalMode &&
      this.$ga
    ) {
      try {
        await this.$ga.event({
          eventCategory:
            'Activity',

          eventAction:
            'Started optional Mode',

          eventValue: true,
        })
      } catch (_) {}
    }
  },

  // Return to the last page, preferring the dashboard for active users
  async returnToLastPage({
    state,
    rootGetters,
  }) {
    const user =
      rootGetters['auth/getUser']

    const currentPath =
      (
        this.$router &&
        this.$router.currentRoute &&
        this.$router.currentRoute.path
      ) || ''

    if (!user) {
      if (
        currentPath !== '/' &&
        this.$router
      ) {
        try {
          await this.$router.push({
            path: '/',
          })
        } catch (_) {}
      }

      return
    }

    const navigationHistory =
      state.navigation_history || {}

    const navigationKeys =
      Object.keys(
        navigationHistory
      )

    const isPristineHistory =
      navigationKeys.length === 0 ||
      (
        navigationKeys.length === 1 &&
        navigationKeys[0] === '1-0'
      )

    const currentPage =
      state.current_page || '1-0'

    const isPristinePage =
      currentPage === '1-0'

    const noProgress =
      isPristineHistory &&
      isPristinePage

    const target =
      noProgress
        ? '/page/1-0'
        : '/dashboard'

    if (
      currentPath === target ||
      !this.$router
    ) {
      return
    }

    try {
      await this.$router.push({
        path: target,
      })
    } catch (_) {
      // Ignore duplicate-navigation errors
    }
  },

  // Determine the next and previous pages for navigation arrows
  setNavigationArrowPages({
    commit,
    state,
  }) {
    const parseSlug = (slug) => {
      const match =
        String(slug || '').match(
          /^(\d+[A-Z]?)-(\d+)$/i
        )

      return match
        ? {
            block: match[1],
            step: Number(match[2]),
          }
        : null
    }

    const source =
      state.optional_mode
        ? state.optional_history
        : state.navigation_history

    let keys =
      helpers.getKeysInOrder(source) ||
      []

    if (
      state.review_mode &&
      Array.isArray(keys)
    ) {
      const module =
        String(
          state.current_module || ''
        )

      keys = keys.filter((key) => {
        if (
          !key ||
          key === 'Review'
        ) {
          return false
        }

        const parsed =
          parseSlug(key)

        if (!parsed) {
          return false
        }

        return (
          String(parsed.block) ===
          module
        )
      })
    }

    if (!keys.length) {
      commit(
        'setLastViewedPage',
        null
      )

      commit(
        'setNextViewedPage',
        null
      )

      return
    }

    const current =
      parseSlug(
        state.current_page
      )

    const fromExactIndex = () => {
      const index =
        keys.indexOf(
          state.current_page
        )

      if (index === -1) {
        return {
          prev: null,
          next: null,
        }
      }

      return {
        prev:
          keys[index - 1] ||
          null,
        next:
          keys[index + 1] ||
          null,
      }
    }

    const fromClosestVisited = () => {
      if (!current) {
        return {
          prev: null,
          next: null,
        }
      }

      let lower = null
      let higher = null

      for (const key of keys) {
        const parsed =
          parseSlug(key)

        if (!parsed) {
          continue
        }

        if (
          parsed.block !==
          current.block
        ) {
          continue
        }

        if (
          parsed.step <
          current.step
        ) {
          lower = key
        }

        if (
          parsed.step >
            current.step &&
          !higher
        ) {
          higher = key
        }
      }

      return {
        prev: lower,
        next: higher,
      }
    }

    let prevTarget = null
    let nextTarget = null

    if (
      keys.includes(
        state.current_page
      )
    ) {
      const {
        prev,
        next,
      } = fromExactIndex()

      prevTarget = prev
      nextTarget = next
    } else {
      const {
        prev,
        next,
      } = fromClosestVisited()

      prevTarget = prev
      nextTarget = next
    }

    if (
      !state.review_mode &&
      !nextTarget
    ) {
      const parsed =
        parseSlug(
          state.current_page
        )

      if (parsed) {
        nextTarget =
          `${parsed.block}-${parsed.step + 1}`
      }
    }

    if (
      !state.review_mode &&
      !prevTarget
    ) {
      const parsed =
        parseSlug(
          state.current_page
        )

      if (parsed) {
        prevTarget =
          `${parsed.block}-${Math.max(
            1,
            parsed.step - 1
          )}`
      }
    }

    commit(
      'setLastViewedPage',
      prevTarget
    )

    commit(
      'setNextViewedPage',
      nextTarget
    )
  },

  processMenuItems({
    commit,
    state,
    rootGetters,
  }) {
    const content =
      rootGetters[
        'content/getModuleContent'
      ]

    if (
      !content ||
      (
        !content.items &&
        !content.optional &&
        !content.extra
      )
    ) {
      commit(
        'setMenuItems',
        []
      )

      return
    }

    const moduleContent = []

    const navigationHistory =
      state.navigation_history &&
      typeof state.navigation_history ===
        'object'
        ? state.navigation_history
        : {
            '1-0': null,
          }

    const navigationKeys =
      Object.keys(
        navigationHistory || {}
      )

    const pristine =
      navigationKeys.length === 1 &&
      navigationKeys[0] === '1-0'

    if (
      pristine &&
      Array.isArray(content.items) &&
      content.items.length
    ) {
      const first =
        helpers.clone(
          content.items[0]
        )

      first.complete = false
      first.current = true
      first.icon = ''
      first.optional = false

      commit(
        'setMenuItems',
        [
          first,
          ...(
            content.extra
              ? [
                  helpers.clone(
                    content.extra
                  ),
                ]
              : []
          ),
        ]
      )

      return
    }

    const completedModules = []

    helpers
      .getModulesCompleted(
        navigationHistory
      )
      .forEach((moduleId) => {
        if (
          !completedModules.includes(
            moduleId
          )
        ) {
          completedModules.push(
            moduleId
          )
        }
      })

    completedModules.forEach(
      (moduleId) => {
        const found =
          (
            content.items || []
          ).filter((item) => {
            const id =
              item &&
              (
                item.id != null
                  ? item.id
                  : item.category
              )

            return (
              String(id) ===
                String(moduleId) ||
              String(
                item.category
              ) === String(moduleId)
            )
          })

        if (
          found &&
          found.length > 0
        ) {
          const cloned =
            helpers.clone(
              found[0]
            )

          cloned.complete =
            state.current_module !==
            cloned.category

          cloned.current =
            state.current_module ===
            cloned.category

          cloned.icon =
            state.current_module ===
            cloned.category
              ? 'mdi-check'
              : ''

          cloned.optional = false

          moduleContent.push(
            cloned
          )
        }
      }
    )

    const optionalCompleted =
      (
        state.optional_completed || []
      ).map(String)

    const optionalAvailable =
      (
        state.optional_available || []
      ).map(String)

    const optionalItems =
      content.optional || []

    const completedOptional =
      optionalItems
        .filter((item) =>
          optionalCompleted.includes(
            String(item.category)
          )
        )
        .map((item) => {
          const cloned =
            helpers.clone(item)

          cloned.optional = true
          cloned.finished = true
          cloned.icon = 'mdi-check'
          cloned.editable = true

          return cloned
        })

    const availableOptional =
      optionalItems
        .filter((item) =>
          optionalAvailable.includes(
            String(item.category)
          )
        )
        .filter((item) =>
          !optionalCompleted.includes(
            String(item.category)
          )
        )
        .map((item) => {
          const cloned =
            helpers.clone(item)

          cloned.optional = true
          cloned.finished = true
          cloned.icon =
            'mdi-lightbulb-on'
          cloned.editable = true

          return cloned
        })

    const byCategory = {}

    completedOptional.forEach(
      (module) => {
        byCategory[
          module.category
        ] = module
      }
    )

    availableOptional.forEach(
      (module) => {
        if (
          !byCategory[
            module.category
          ]
        ) {
          byCategory[
            module.category
          ] = module
        }
      }
    )

    Object.values(
      byCategory
    ).forEach((optional) => {
      moduleContent.push(
        optional
      )
    })

    if (
      state.current_module !==
        'Review' &&
      content.extra
    ) {
      moduleContent.push(
        helpers.clone(
          content.extra
        )
      )
    }

    if (
      moduleContent.length === 0 &&
      Array.isArray(content.items) &&
      content.items.length
    ) {
      const first =
        helpers.clone(
          content.items[0]
        )

      first.complete = false
      first.current = true
      first.icon = ''
      first.optional = false

      moduleContent.push(first)

      if (content.extra) {
        moduleContent.push(
          helpers.clone(
            content.extra
          )
        )
      }
    }

    commit(
      'setMenuItems',
      moduleContent
    )
  },

  async removeOptionalModule(
    {
      commit,
      dispatch,
      state,
    },
    module
  ) {
    const moduleId =
      String(module)

    const wasAvailable =
      (
        state.optional_available || []
      )
        .map(String)
        .includes(moduleId)

    const wasCompleted =
      (
        state.optional_completed || []
      )
        .map(String)
        .includes(moduleId)

    const hadHistory =
      Object.keys(
        state.optional_history || {}
      ).some(
        (page) =>
          String(page).split('-')[0] ===
          moduleId
      )

    if (
      !wasAvailable &&
      !wasCompleted &&
      !hadHistory
    ) {
      return
    }

    commit(
      'removeOptionalModule',
      moduleId
    )

    await dispatch(
      'storeOptionalHistory'
    )

    await dispatch(
      'processMenuItems'
    )
  },

  // Unlock optional modules and notify only for genuinely new modules
  async unlockOptionalModules(
    {
      commit,
      dispatch,
      state,
    },
    unlocked
  ) {
    const incoming = (
      Array.isArray(unlocked)
        ? unlocked
        : [unlocked]
    )
      .map(String)
      .filter(Boolean)

    const coreModules =
      helpers
        .getModulesCompleted(
          state.navigation_history || {}
        )
        .map(String)

    const completedOptional =
      (
        state.optional_completed || []
      ).map(String)

    const currentlyAvailable =
      (
        state.optional_available || []
      ).map(String)

    const eligibleIncoming = [
      ...new Set(incoming),
    ].filter(
      (module) =>
        !coreModules.includes(module) &&
        !completedOptional.includes(module)
    )

    const cleanedAvailable =
      currentlyAvailable.filter(
        (module) =>
          !coreModules.includes(module) &&
          !completedOptional.includes(module)
      )

    const newlyUnlocked =
      eligibleIncoming.filter(
        (module) =>
          !cleanedAvailable.includes(module)
      )

    const nextAvailable = [
      ...new Set([
        ...cleanedAvailable,
        ...newlyUnlocked,
      ]),
    ]

    commit(
      'setOptionalAvailable',
      nextAvailable
    )

    await dispatch(
      'storeOptionalHistory'
    )

    if (
      newlyUnlocked.length > 0 &&
      !state.review_mode
    ) {
      commit(
        'setNotifierInfo',
        newlyUnlocked
      )

      commit(
        'setDisplayNotification',
        true
      )
    } else {
      commit(
        'setNotifierInfo',
        []
      )

      commit(
        'setDisplayNotification',
        false
      )
    }

    await dispatch(
      'processMenuItems'
    )
  },
}

export const getters = {
  // Find the next page
  getNextPage: (state) => {
    return state.next_page
  },

  // Find the current page
  getCurrentPage: (state) => {
    return state.current_page
  },

  isReviewMode: (state) =>
    state.review_mode,

  // Get the completion state of the page
  getPageCompleted: (state) => {
    return state.page_completed
  },

  getOptionalHistory: (state) =>
    state.optional_history,

  // Get the current module number
  getCurrentModule: (state) => {
    return state.current_module
  },

  // Get the page progress value
  getPageProgress: (state) => {
    return state.progress
  },

  getLastViewedPage: (state) => {
    return state.last_viewed_page
  },

  getNextViewedPage: (state) => {
    return state.next_viewed_page
  },

  getDisplayNotificationStatus: (state) => {
    return state.display_notification
  },

  getNotifierInfo: (state) => {
    return state.notifier_info
  },

  getGoingBack: (state) => {
    return state.going_back
  },

  getOptionalCompleted: (state) => {
    return state.optional_completed
  },

  getOptionalAvailable: (state) => {
    return state.optional_available
  },

  getNavigationHistory: (state) => {
    return state.navigation_history
  },

  getOptionalProgressByModule:
    (state) =>
    (module) => {
      if (!module) {
        return 0
      }

      const keys =
        Object.keys(
          state.optional_history || {}
        )

      const regex =
        new RegExp(
          `^${module}-([0-9]+)$`
        )

      let max = null

      for (const key of keys) {
        const match =
          String(key).match(regex)

        if (match) {
          const number =
            Number(match[1])

          if (
            !Number.isNaN(number) &&
            (
              max == null ||
              number > max
            )
          ) {
            max = number
          }
        }
      }

      if (max == null) {
        return 0
      }

      const denominator =
        OPTIONAL_MAX_INDEX[
          String(module)
        ]

      if (!denominator) {
        return 0
      }

      let percentage =
        Math.round(
          (
            max /
            denominator
          ) * 100
        )

      if (
        percentage > 99 &&
        !(
          state.optional_completed ||
          []
        ).includes(
          String(module)
        )
      ) {
        percentage = 99
      }

      return percentage
    },

  getOptionalResumePageByModule:
    (
      state,
      getters,
      rootState,
      rootGetters
    ) =>
    (module) => {
      if (!module) {
        return null
      }

      const keys =
        Object.keys(
          state.optional_history || {}
        )

      const regex =
        new RegExp(
          `^${module}-([0-9]+)$`
        )

      let max = null

      for (const key of keys) {
        const match =
          String(key).match(regex)

        if (match) {
          const number =
            Number(match[1])

          if (
            !Number.isNaN(number) &&
            (
              max == null ||
              number > max
            )
          ) {
            max = number
          }
        }
      }

      if (max != null) {
        return `${module}-${max}`
      }

      const modules =
        rootGetters[
          'content/getModuleContent'
        ] || {}

      const pools = [
        modules.optional || [],
        modules.items || [],
      ]

      for (const list of pools) {
        const found =
          (list || []).find(
            (item) =>
              String(
                item.category
              ) === String(module)
          )

        if (
          found &&
          found.url
        ) {
          const slug =
            String(
              found.url
            ).replace(
              /^\/page\//,
              ''
            )

          return (
            slug ||
            `${module}-0`
          )
        }
      }

      return `${module}-0`
    },

  // Retrieve existing answers for the current page
  getPageAnswers: (state) => {
    const source =
      state.optional_mode
        ? state.optional_history
        : state.navigation_history

    if (
      source &&
      containsKey(
        source,
        state.current_page
      )
    ) {
      const value =
        source[
          state.current_page
        ]

      if (
        value === undefined ||
        value === null
      ) {
        return []
      }

      return value
    }

    return null
  },

  getOptionalModStatus: (state) => {
    return state.optional_mode
  },

  getFinishedModules: (state) => {
    return state.finished_modules
  },

  getMenuItems: (state) => {
    return state.menu_items
  },

  getModuleColor: (state) => {
    return (
      state.module_color ||
      'primary lighten-3'
    )
  },

  getPendingRequests: (state) => {
    return state.pendingRequests
  },

  isHydrating: (state) => {
    return state.hydrating
  },
}

export const helpers = {
  // Clone a JSON-compatible object
  clone(data) {
    return JSON.parse(
      JSON.stringify(data)
    )
  },

  parseIfString(json) {
    if (Array.isArray(json)) {
      return json
    }

    if (
      json &&
      typeof json === 'object'
    ) {
      return json
    }

    try {
      return JSON.parse(json)
    } catch (e) {
      return json
    }
  },

  // Arrange navigation keys in the correct sequence
  getKeysInOrder(json) {
    if (
      !json ||
      typeof json !== 'object'
    ) {
      return []
    }

    const keys =
      Object.keys(json)

    return keys.sort((a, b) =>
      a.localeCompare(
        b,
        undefined,
        {
          numeric: true,
          ignorePunctuation: true,
        }
      )
    )
  },

  getModulesCompleted(
    navigation_history
  ) {
    if (
      !navigation_history ||
      typeof navigation_history !==
        'object' ||
      Array.isArray(
        navigation_history
      )
    ) {
      return []
    }
  
    const modules = []
  
    Object.keys(
      navigation_history
    ).forEach((page) => {
      if (!page) {
        return
      }
  
      /*
       * Review must only become a dashboard module after the
       * user has actually reached the Review page.
       *
       * Before that happens, navigation_history has no "Review"
       * key, so this function will not return "Review" and the
       * dashboard will not display its card.
       *
       * Once the user reaches Review, the key is preserved and
       * the card remains available after completing bonus modules
       * or returning to the dashboard later.
       */
      const moduleId =
        page === 'Review'
          ? 'Review'
          : String(page).split(
              '-'
            )[0]
  
      if (
        moduleId &&
        !modules.includes(
          moduleId
        )
      ) {
        modules.push(
          moduleId
        )
      }
    })
  
    return modules
  },
}